import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import * as fromRoot from '../reducers';
import * as campaignEngage from '../actions/campaign-engage';
import { EngageService } from '../engage/engage.service';

import { CampaignTemplatesModel } from '../models/engage/campaign-templates.model';

import { TemplateDetails } from '../models/engage/template-details.model';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import {
  CampaignSentConfirmationModalComponent
} from '../shared/campaign-form/campaign-sent-confirmation-modal/campaign-sent-confirmation-modal.component';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CampaignInputs } from '../models/engage/campaign-details.model';
import { AudienceSource } from '../meet/dependencies/audience-source';
import { HttpClient } from '@angular/common/http';
import { DigitalMarketingAudienceSource } from '../meet/dependencies/digital-marketing-audience-source';
import { EngageConstants } from '../engage/engage.constants';
import { ErrorHandlerService } from '../core/error-handler/error-handler.service';
import { getAudienceFilterAnalytics } from '../engage/audience-filter-analytics';

@Injectable()
export class CampaignEngageEffects {

  public fetchCampaignTemplates$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        campaignEngage.ActionTypes.FETCH_CAMPAIGN_TEMPLATES
      ),
      concatLatestFrom((action: any) =>
        [this.store.select(fromRoot.getCampaignEngageTemplates), this.store.select(fromRoot.getCampaignType)]),
      switchMap(([action, templates, campaignType]) => {
        if (templates.length == 0) {
          return this.es.getCampaignTemplates(action.campaignType).pipe(
            map((res: CampaignTemplatesModel[]) => {
              return new campaignEngage.FetchCampaignTemplatesSuccess(res)
            }),
            catchError((error) => of(
              new campaignEngage.FetchCampaignTemplatesFailure(
                campaignType,
                this.errorHandler.extractErrorMessage(error)
              )
            ))
          )
        }
        // Return success action with existing templates when they already exist
        return of(new campaignEngage.FetchCampaignTemplatesSuccess(templates))
      })
    )
  });

  public fetchRewardTemplateDetails$: Observable<Action> = createEffect(() => {return this.actions$.pipe(
    ofType(
      campaignEngage.ActionTypes.SELECT_TEMPLATE
    ),
    switchMap((action: any) => this.es.getTemplate(action.payload.id).pipe(
      map((res: TemplateDetails) => {
        return new campaignEngage.SelectTemplateSuccess(res)
      }),
      catchError((error) => of(new campaignEngage.SelectTemplateFailure(this.errorHandler.extractErrorMessage(error))))
    ))
  )});

  public createCampaign$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(campaignEngage.ActionTypes.CREATE_CAMPAIGN),
      switchMap(() =>
        this.store.select(fromRoot.getFeatureEnabled(EngageConstants.dynamicAudiencesEnabled)).pipe(
          take(1),
          switchMap((isEnabled) => {
            if (isEnabled) {
              return this.handleDynamicAudiencesEnabled();
            }
            return this.handleDynamicAudiencesDisabled();
          }),
          catchError((error) => of(new campaignEngage.CreateCampaignFailure(this.errorHandler.extractErrorMessage(error))))
        )
      )
    );
  });

  public createCampaignStart$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      campaignEngage.ActionTypes.CREATE_CAMPAIGN,
      campaignEngage.ActionTypes.UPDATE_CAMPAIGN,
    ),
    tap((campaign) => this.openCampaignSendConfirmationDialog())
  )},{ dispatch: false });

  public createCampaignSuccess$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      campaignEngage.ActionTypes.CREATE_CAMPAIGN_SUCCESS,
      campaignEngage.ActionTypes.CREATE_CAMPAIGN_FAILURE,
      campaignEngage.ActionTypes.UPDATE_CAMPAIGN_SUCCESS,
      campaignEngage.ActionTypes.UPDATE_CAMPAIGN_FAILURE
    ),
    switchMap((action: any) => this.store.select(fromRoot.getCampaignConfirmation(action.payload?.update ?? false)).pipe(take(1))),
    tap((campaign) => this.updateCampaignSendConfirmationDialog(campaign))
  )},{ dispatch: false });

  public updateCampaign$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(campaignEngage.ActionTypes.UPDATE_CAMPAIGN),
      switchMap(() => this.store.select(fromRoot.getCustomerAPIRequestByPage('engage')).pipe(take(1))),
      concatLatestFrom(() =>
        [this.store.select(fromRoot.getCampaignFilterInfo),
          this.store.select(fromRoot.getCreateCampaignRequest),
          this.store.select(fromRoot.shouldGetAllCustomers),
          this.store.select(fromRoot.getEngageAllSelectedCustomerIds),
        ]),
      switchMap(([selectedFilters, filterInfo, createCampaignRequest, getAllCustomers, allCustomerIds]) =>
        this.updateAudienceAndCampaign(
          this.handleAllSpotlightCustomers(selectedFilters, getAllCustomers, allCustomerIds),
          filterInfo,
          createCampaignRequest
        )
      ),
      catchError((error) => of(new campaignEngage.UpdateCampaignFailure(this.errorHandler.extractErrorMessage(error))))
    );
  });

  public cancelCampaign$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(campaignEngage.ActionTypes.CANCEL_CAMPAIGN),
    concatLatestFrom((action: any) => this.store.select(fromRoot.getCreateCampaignRequest)),
    switchMap(([action, campaign]) => this.es.cancelCampaign(action.payload, campaign).pipe(
      map(() => new campaignEngage.CancelCampaignSuccess()),
      catchError((error) => of(new campaignEngage.CancelCampaignFailure(this.errorHandler.extractErrorMessage(error)))))
    )
  )});

  public cancelCurrentReward: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(campaignEngage.ActionTypes.CANCEL_CURRENT_REWARD),
    switchMap((action: any) => this.es.cancelCurrentReward(action.payload).pipe(
      map(() => new campaignEngage.CancelCampaignSuccess()),
      catchError((error) => of(new campaignEngage.CancelCampaignFailure(this.errorHandler.extractErrorMessage(error)))))
    )
  )});

  public cancelCampaignOutcome: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      campaignEngage.ActionTypes.CANCEL_CAMPAIGN_SUCCESS,
      campaignEngage.ActionTypes.CANCEL_CAMPAIGN_FAILURE
    ),
    tap((action: any) => {
      if (action.err) {
        let config = new MatDialogConfig();
        config.data = {
          displayText: 'Campaign could not be canceled. Please try again later.',
          confirmText: 'Back to Calendar',
          hideCancel: true
        };

        this.dialog.open(ConfirmationDialogComponent, config).afterClosed().subscribe(() => {
          this.router.navigate(['/calendar']);
        });
        return;
      }

      this.snackBar.open(
        `Campaign was successfully deleted.`,
        'Dismiss', {
          duration: 5000
        }
      );

      return this.router.navigate(['/calendar']);
    })
  )}, { dispatch :false });

  public fetchRewardAnalytics$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      campaignEngage.ActionTypes.FETCH_CAMPAIGN_DETAILS_SUCCESS,
    ),
    switchMap(() => this.store.select(fromRoot.getRewardAnalyticsEnabled).pipe(take(1))),
    filter((enabled) => enabled),
    concatLatestFrom(() => [
      this.store.select(fromRoot.getSelectedRewardCampaign),
      this.store.select(fromRoot.getSelectedCampaignID),
      this.store.select(fromRoot.getFeatureEnabled(EngageConstants.enhancedAnalyticsMetricsFlag))
    ]),
    switchMap(([, reward, campaignID, useMetrics]) => {
      const analytics$ = useMetrics
        ? this.es.getCampaignRewardAnalytics(campaignID)
        : this.es.getRewardAnalytics(reward.offerBatchID);
      return analytics$.pipe(
        map((res) => new campaignEngage.FetchRewardAnalyticsSuccess(res)),
        catchError((error) => of(new campaignEngage.FetchRewardAnalyticsFailure(this.errorHandler.extractErrorMessage(error))))
      );
    })
  )});

  private campaignSentModal: MatDialogRef<CampaignSentConfirmationModalComponent, any>;

  constructor(
    private readonly actions$: Actions,
    private readonly es: EngageService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly store: Store<fromRoot.State>,
    private readonly dialog: MatDialog,
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlerService,
  ) { }

  private readonly audienceSource: AudienceSource = new DigitalMarketingAudienceSource(this.http)

  private openCampaignSendConfirmationDialog() {
    let config: MatDialogConfig = {
      autoFocus: false,
      disableClose: true,
      panelClass:'dialog-responsive',
      data: { loading: true }
    }
    this.campaignSentModal = this.dialog.open(CampaignSentConfirmationModalComponent, config);
  }

  private updateCampaignSendConfirmationDialog(campaign) {
    if (this.campaignSentModal?.getState() === MatDialogState.OPEN) {
      this.campaignSentModal.componentInstance.campaign = { ...campaign, loading: false };
    }
  }

  private createAudienceAndCampaign(
    selectedFilters: any, filterInfo: any, createCampaignRequest: any, audienceFilterAnalytics = {}
  ): Observable<Action> {
    return this.audienceSource.createAudience(
      filterInfo.location.locationNumber, selectedFilters, filterInfo.groupNow, createCampaignRequest.sendingOptions.sendDate
    ).pipe(
      switchMap((audienceID) => {
        return this.es.createCampaign({ ...createCampaignRequest, audienceID }, audienceFilterAnalytics).pipe(
          map((campaignID: string) => new campaignEngage.CreateCampaignSuccess(campaignID))
        );
      })
    );
  }

  private updateAudienceAndCampaign(selectedFilters: any, filterInfo: any, createCampaignRequest): Observable<Action> {
    if (!filterInfo.audienceID || filterInfo.disableCustomerSelection) {
      return this.es.updateCampaign({ ...createCampaignRequest }).pipe(
        map((campaignID: string) => new campaignEngage.UpdateCampaignSuccess({ campaignID, update: true }))
      )
    }
    return this.audienceSource.updateAudience(
      filterInfo.location.locationNumber, selectedFilters, filterInfo.groupNow, filterInfo.audienceID,
      createCampaignRequest.sendingOptions.sendDate).pipe(
      switchMap((audienceID) => {
        return this.es.updateCampaign({ ...createCampaignRequest, audienceID }).pipe(
          map((campaignID: string) => new campaignEngage.UpdateCampaignSuccess({ campaignID, update: true }))
        );
      })
    );
  }

  private handleDynamicAudiencesEnabled(): Observable<Action> {
    return this.store.select(fromRoot.getCustomerAPIRequestByPage('engage')).pipe(
      take(1),
      concatLatestFrom(() => [
        this.store.select(fromRoot.getCampaignFilterInfo),
        this.store.select(fromRoot.getCreateCampaignRequest),
        this.store.select(fromRoot.shouldGetAllCustomers),
        this.store.select(fromRoot.getEngageAllSelectedCustomerIds),
      ]),
      switchMap(([selectedFilters, filterInfo, createCampaignRequest, getAllCustomers, allCustomerIds]) =>
        this.createAudienceAndCampaign(
          this.handleAllSpotlightCustomers(selectedFilters, getAllCustomers, allCustomerIds),
          filterInfo,
          createCampaignRequest,
          getAudienceFilterAnalytics(selectedFilters, getAllCustomers && allCustomerIds?.length > 0)
        )
      )
    );
  }

  // the 'Select All Customers' button on meet/filter does not get treated like a normal filter
  // in the case its selection we want to inject them as an include-customers filter
  // before we do the conversion from old -> new filters
  private handleAllSpotlightCustomers(selectedFilters: any, shouldGetAllCustomers: boolean, allCustomerIds: string[]): any {
    // comes from state to determine if the user has selected the 'Select All Customers' option on the filter page
    if (!shouldGetAllCustomers) {
      return selectedFilters;
    }
    // if allCustomers is null, undefined, empty, or len 0
    if (!allCustomerIds?.length) {
      return selectedFilters;
    }
    return {
      ...selectedFilters,
      selectedFilters: [
        ...selectedFilters.selectedFilters,
        {
          id: 'include-customers',
          cfaIdList: allCustomerIds,
        }
      ]
    };
  }

  private handleDynamicAudiencesDisabled(): Observable<Action> {
    return this.store.select(fromRoot.getCreateCampaignRequest).pipe(
      take(1),
      switchMap((campaign: CampaignInputs) =>
        this.es.createCampaign(campaign).pipe(
          map((campaignID: string) => new campaignEngage.CreateCampaignSuccess(campaignID))
        )
      )
    );
  }
}
